import { useEffect, useState } from "react";
import { Icon } from "@iconify/react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Checkbox } from "antd";
import { signUpApi, toAuthResult } from "@/portals/patient/api/patientAuthApi";
import { getInvitePreviewApi } from "@/portals/patient/api/familyApi";
import { saveAuthFlowState } from "./authFlowState";
import { startGoogleSignIn } from "./googleAuth";
import { InviteUnavailableNotice } from "./InviteUnavailableNotice";
import { toastError, toastSuccess } from "@/shared/lib/toast";
import { AuthTextField, AuthPasswordField } from "@/shared/components/AuthField";
import { AuthSubmitButton } from "@/shared/components/AuthSubmitButton";
import { Button } from "@/shared/components/button";
import { CalendarDateField } from "@/shared/components/CalendarDateField";
import { validateFields } from "@/shared/lib/validators";
import { cn } from "@/shared/lib/utils";
import { CONFIRM_PASSWORD_RULES, PASSWORD_MISMATCH_MESSAGE, SIGNUP_PASSWORD_RULES } from "@/shared/lib/passwordRules";
import { AuthHeading } from "@/shared/components/AuthHeading";
import { RequiredMark } from "@/shared/components/RequiredMark";

function GoogleIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" xmlns="http://www.w3.org/2000/svg">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
    </svg>
  );
}

/** A real calendar date (YYYY-MM-DD) before today, in local time. */
export function isPastDate(value: string): boolean {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return false;
  const [year, month, day] = [Number(match[1]), Number(match[2]) - 1, Number(match[3])];
  const date = new Date(year, month, day);
  if (date.getFullYear() !== year || date.getMonth() !== month || date.getDate() !== day) return false;
  const now = new Date();
  return date < new Date(now.getFullYear(), now.getMonth(), now.getDate());
}

interface InviteContext {
  patientName: string;
  categories: string[];
}

export function PatientSignUpPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get("token");
  const [loading, setLoading] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  // YYYY-MM-DD. Required: uploads are checked against the name + date of birth.
  const [dob, setDob] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [terms, setTerms] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [inviteContext, setInviteContext] = useState<InviteContext | null>(null);
  const [emailLocked, setEmailLocked] = useState(false);
  // True only when a token WAS present but didn't resolve to a live invite —
  // cancelled, declined, already used, or expired. Blocks the form entirely
  // instead of silently falling back to a normal signup: someone who clicked
  // a real invite email link deserves to be told what happened to it, not
  // land on a blank form with no explanation (see the branded message below).
  const [inviteUnavailable, setInviteUnavailable] = useState(false);
  const restoredValues = (location.state as { signupForm?: Record<string, unknown> } | null)?.signupForm;

  useEffect(() => {
    if (!restoredValues) return;
    if (typeof restoredValues.firstName === "string") setFirstName(restoredValues.firstName);
    if (typeof restoredValues.lastName === "string") setLastName(restoredValues.lastName);
    if (typeof restoredValues.dob === "string") setDob(restoredValues.dob);
    if (typeof restoredValues.email === "string") setEmail(restoredValues.email);
    if (typeof restoredValues.password === "string") setPassword(restoredValues.password);
    if (typeof restoredValues.confirmPassword === "string") setConfirmPassword(restoredValues.confirmPassword);
    if (typeof restoredValues.terms === "boolean") setTerms(restoredValues.terms);
  }, [restoredValues]);

  // Resolves a caregiver invite link's token so the email field can be
  // pre-filled and locked, and the inviting patient shown. A token that
  // doesn't resolve (cancelled, declined, already used, expired) shows the
  // dedicated "no longer available" message instead (see inviteUnavailable) —
  // never a silent fallback to a plain signup form for a real invite link.
  useEffect(() => {
    if (!inviteToken) return;
    let cancelled = false;
    getInvitePreviewApi(inviteToken).then(({ data }) => {
      if (cancelled) return;
      if (!data) {
        setInviteUnavailable(true);
        return;
      }
      setEmail(data.email);
      setEmailLocked(true);
      setInviteContext({ patientName: data.patientName, categories: data.categories });
    });
    return () => {
      cancelled = true;
    };
  }, [inviteToken]);

  // Leaves for Google's account chooser; only comes back here on a failure to start.
  const [googleLoading, setGoogleLoading] = useState(false);
  const handleGoogle = async () => {
    setGoogleLoading(true);
    const error = await startGoogleSignIn();
    if (error) {
      setGoogleLoading(false);
      toastError(error);
    }
  };

  const clearError = (field: string) => {
    setErrors((prev) => {
      if (!prev[field]) return prev;
      const { [field]: _removed, ...rest } = prev;
      return rest;
    });
  };

  const openLegalPage = (path: string) => {
    const signupForm = { firstName, lastName, dob, email, password, confirmPassword, terms };
    navigate("/patient/auth/signup", { replace: true, state: { signupForm } });
    navigate(path, { state: { from: "/patient/auth/signup", signupForm } });
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    const fieldErrors = validateFields(
      { firstName, lastName, dob, email, password, confirmPassword, terms },
      {
        firstName: [{ required: true, message: "First name is required" }],
        lastName: [{ required: true, message: "Last name is required" }],
        dob: [
          { required: true, message: "Date of birth is required" },
          { validator: (value) => (isPastDate(String(value)) ? undefined : "Date of birth must be in the past") },
        ],
        email: [
          { required: true, message: "Email is required" },
          { type: "email", message: "Enter a valid email address" },
        ],
        password: SIGNUP_PASSWORD_RULES,
        confirmPassword: CONFIRM_PASSWORD_RULES,
        terms: [{ validator: (value) => (value ? undefined : "Please accept the terms") }],
      },
    );
    setErrors(fieldErrors);
    if (Object.keys(fieldErrors).length > 0) return;

    if (password !== confirmPassword) {
      toastError(PASSWORD_MISMATCH_MESSAGE);
      return;
    }

    setLoading(true);
    const result = toAuthResult(await signUpApi(firstName, lastName, dob, email, password, confirmPassword));
    setLoading(false);

    if (result.success) {
      saveAuthFlowState({ email, fromSignup: true });
      toastSuccess("OTP has been sent successfully. Please check your email.");
      navigate("/patient/auth/verify-otp", { state: { email, fromSignup: true, inviteContext } });
    } else {
      // The backend's message for a duplicate account confirms the email is
      // registered — don't relay that account-enumeration signal to the user.
      const isDuplicateEmail = /already (?:exists|registered|in use)|account.*already/i.test(result.error ?? "");
      toastError(isDuplicateEmail ? "We can't proceed with this email right now. Please try a different email" : result.error ?? "Unable to create account.");
    }
  };

  if (inviteUnavailable) {
    return <InviteUnavailableNotice continueTo="/patient/auth/signup" continueLabel="Continue to a regular sign up" />;
  }

  return (
    <div className="w-full">
      <AuthHeading title={<>Welcome To <span className="font-extrabold tracking-tight">MedSyncIQ</span></>} subtitle="Please enter your details" className="mb-9" />

      {inviteContext && (
        <div className="!mb-6 flex items-start gap-2.5 rounded-[20px] border border-[#F36F8F]/40 bg-[#FFF7F8] p-3.5">
          <Icon icon="lucide:heart-handshake" className="mt-0.5 h-4 w-4 shrink-0 text-[#E4002B]" />
          <p className="text-xs leading-relaxed text-[#374151]">
            <span className="font-semibold">{inviteContext.patientName}</span> invited you to view their health records
            {inviteContext.categories.length > 0 && (
              <> — <span className="font-medium">{inviteContext.categories.join(", ")}</span></>
            )}
            . Finish setting up your account to accept.
          </p>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="mb-5 grid grid-cols-2 gap-3">
          <AuthTextField
            required
            label="First Name"
            placeholder="John"
            autoComplete="given-name"
            icon="lucide:user-round"
            value={firstName}
            onChange={(v) => { setFirstName(v); clearError("firstName"); }}
            error={errors.firstName}
            disabled={loading}
            className="mb-0"
          />
          <AuthTextField
            required
            label="Last Name"
            placeholder="Doe"
            autoComplete="family-name"
            icon="lucide:user-round"
            value={lastName}
            onChange={(v) => { setLastName(v); clearError("lastName"); }}
            error={errors.lastName}
            disabled={loading}
            className="mb-0"
          />
        </div>

        <AuthTextField
          required
          label="Email address"
          placeholder="john.doe@example.com"
          autoComplete="email"
          icon="lucide:mail"
          value={email}
          onChange={(v) => { setEmail(v); clearError("email"); }}
          error={errors.email}
          disabled={emailLocked || loading}
        />

        <div className="mb-5">
          <label className="mb-2 block text-sm font-medium text-[#374151]">Date of Birth<RequiredMark /></label>
          {/* CalendarDateField has no disabled prop — inert blocks clicks and
              keyboard focus on the whole picker while sign-up is in flight. */}
          <div
            data-testid="signup-dob"
            inert={loading}
            className={cn(
              "flex h-[42px] items-center rounded-[14px] border bg-white px-4 hover:border-[#E4002B]/50",
              errors.dob ? "border-[#E4002B]" : "border-[#E5E7EB]",
              loading && "opacity-60",
            )}
          >
            <span className="mr-2.5 flex items-center gap-2.5">
              <Icon icon="lucide:id-card" className="h-4 w-4 text-[#374151]" />
              <span className="h-5 w-px shrink-0 bg-[#DADDE5]" />
            </span>
            <CalendarDateField
              value={dob}
              onChange={(v) => { setDob(v); clearError("dob"); }}
              placeholder="Select date of birth"
              disableToday
              bare
            />
          </div>
          {errors.dob && <p className="!mt-1 text-xs text-[#E4002B]">{errors.dob}</p>}
        </div>

        <AuthPasswordField
          required
          label="Password"
          placeholder="Password"
          autoComplete="new-password"
          icon="lucide:lock"
          value={password}
          onChange={(v) => { setPassword(v); clearError("password"); }}
          error={errors.password}
          disabled={loading}
        />

        <AuthPasswordField
          required
          label="Confirm Password"
          placeholder="Confirm Password"
          autoComplete="new-password"
          icon="lucide:lock"
          value={confirmPassword}
          onChange={(v) => { setConfirmPassword(v); clearError("confirmPassword"); }}
          error={errors.confirmPassword}
          disabled={loading}
        />

        <div className="mb-6">
          <div className="flex items-center gap-2">
            <Checkbox
              checked={terms}
              disabled={loading}
              onChange={(e) => { setTerms(e.target.checked); clearError("terms"); }}
              className="[&_.ant-checkbox]:!rounded-full [&_.ant-checkbox]:!border-[#E4002B]/50 [&_.ant-checkbox-checked]:!bg-[#E4002B]/50 [&_.ant-checkbox-checked]:!border-[#E4002B]/50 [&_.ant-checkbox-checked::after]:!border-white [&_.ant-checkbox-checked_.ant-checkbox-inner::after]:!border-white"
            />
            <span className="text-[13px] text-[#6F7280]">
              I agree to the{" "}
              <Button
                variant="link"
                disabled={loading}
                onClick={() => openLegalPage("/terms-of-use")}
                className="!h-auto !p-0 !font-semibold !text-[#E4002B] !underline !underline-offset-2 hover:!text-[#E4002B]"
              >
                Terms of Use
              </Button>{" "}
              and{" "}
              <Button
                variant="link"
                disabled={loading}
                onClick={() => openLegalPage("/privacy-policy")}
                className="!h-auto !p-0 !font-semibold !text-[#E4002B] !underline !underline-offset-2 hover:!text-[#E4002B]"
              >
                Privacy Policy
              </Button>
            </span>
          </div>
          {errors.terms && <p className="!mt-1 text-[11px] text-[#E4002B]">{errors.terms}</p>}
        </div>

        <div className="flex flex-col gap-5">
          <AuthSubmitButton loading={loading}>Sign Up</AuthSubmitButton>

          <Button
            variant="outline"
            disabled={loading || googleLoading}
            onClick={handleGoogle}
            className="!flex !h-auto !w-full !items-center !justify-center !gap-2.5 !rounded-[14px] !border-[#E5E7EB] !bg-white !py-3 !text-sm !font-medium !text-[#374151] hover:!border-[#E5E7EB] hover:!bg-[#F9FAFB] hover:!text-[#374151]"
          >
            <GoogleIcon />
            Continue with Google
          </Button>
        </div>
      </form>

      <p className="!mt-5 text-center text-sm text-[#9CA3AF]">
        Already have an account?{" "}
        <Button
          variant="link"
          disabled={loading}
          onClick={() => navigate("/patient/auth/signin")}
          className="!h-auto !p-0 !text-sm !font-semibold !text-[#E4002B] hover:!text-[#E4002B] hover:!underline disabled:!no-underline"
        >
          Sign in
        </Button>
      </p>
    </div>
  );
}
